import React, { useMemo, useState } from 'react';
import { Stack, Button, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Add from '@mui/icons-material/Add';
import { TagDropdown } from '@travelity/form';
import { Tag } from '@travelity/ui';
import CloseIcon from '@mui/icons-material/Close';
import { useFormContext } from 'react-hook-form';
import {
    convertNameDtoToFullName,
    PersonNameData,
    SourceType,
} from '@travelity/api';
import { useSelectOptions } from '../../../../hooks';
import { SourceModal } from '../../../source-modal';

export interface DependencySelectionProps {
    editing: boolean;
    capacities: { capacityId: string; name: string }[];
    schedules: { scheduleId: string; name: string }[];
    prefix?: string;
    productId?: string;
    selectedCapacity: { name: string };
    selectedSchedule: { name: string };
    selectedSource: { name: string } | undefined;
}

const possibleDependencyOptions = [
    { label: 'Capacity', value: 'capacity' },
    { label: 'Schedule', value: 'schedule' },
    { label: 'Source', value: 'source' },
];

const DependencySelection: React.FC<DependencySelectionProps> = ({
    editing,
    capacities,
    schedules,
    selectedCapacity,
    selectedSchedule,
    selectedSource,
    prefix,
    productId,
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const formPrefix = prefix ? `${prefix}.` : '';
    const [sourceModalOpen, setSourceModalOpen] = React.useState(false);
    const { watch, setValue } = useFormContext();
    const capacity = watch(`${formPrefix}capacity`);
    const schedule = watch(`${formPrefix}schedule`);
    const source = editing ? watch(`${formPrefix}source`) : selectedSource;

    const sourceValue = useMemo(() => {
        if (!source) {
            return '';
        }
        if (source.sourceType === SourceType.DIRECT) {
            return t(source.sourceName, { ns: 'team' });
        }

        const name = source.referral.name?.first
            ? convertNameDtoToFullName(source.referral.name as PersonNameData)
            : source.referral.name;
        return source.referral.company || name || source.referral.email || '';
    }, [source]);

    const capacityOptions = useSelectOptions(capacities, 'name', 'id', true);

    const scheduleOptions = useSelectOptions(schedules, 'name', 'id', true);

    const dependencyOptions = useMemo(() => {
        return possibleDependencyOptions
            .filter(d => d.value !== 'capacity' || !capacity)
            .filter(d => d.value !== 'capacity' || !!capacities.length)
            .filter(d => d.value !== 'schedule' || !schedule)
            .filter(d => d.value !== 'schedule' || !!schedules.length)
            .filter(d => d.value !== 'source' || !source);
    }, [capacity, schedule]);

    if (!editing && !selectedSchedule && !selectedCapacity && !selectedSource)
        return null;

    return (
        <Stack
            direction="column"
            gap={1}
            alignItems={editing ? 'center' : 'flex-start'}
            sx={{ mb: editing && dependencyOptions.length ? 0 : 1 }}
        >
            {editing ? (
                <>
                    {capacity && (
                        <TagDropdown
                            label="Capacity"
                            name={`${formPrefix}capacity`}
                            options={capacityOptions}
                            valueAsObject
                            valueKey="capacityId"
                            optionsSx={{
                                maxWidth: '88px',
                            }}
                            autoOpenEmpty
                            enableRemove
                        />
                    )}
                    {schedule && (
                        <TagDropdown
                            label="Schedule"
                            name={`${formPrefix}schedule`}
                            options={scheduleOptions}
                            valueAsObject
                            valueKey="scheduleId"
                            optionsSx={{
                                maxWidth: '88px',
                            }}
                            autoOpenEmpty
                            enableRemove
                        />
                    )}
                    {sourceValue && (
                        <Tag
                            label="Source"
                            value={sourceValue}
                            deleteIcon={
                                <Tooltip title="Remove">
                                    <CloseIcon />
                                </Tooltip>
                            }
                            sx={{
                                '&&&:hover': {
                                    boxShadow:
                                        '0px 1px 12px rgba(178, 185, 205, 0.6)',
                                },
                                '& > .MuiChip-deleteIconSmall': {
                                    ml: 0.5,
                                    mr: 0,
                                    fontSize: '14px',
                                    color: '#9198AA',
                                    lineHeight: '14px',
                                    '&: hover': {
                                        color: '#9198AA',
                                    },
                                },
                            }}
                            onDelete={() =>
                                setValue(`${formPrefix}source`, undefined, {
                                    shouldDirty: true,
                                })
                            }
                        />
                    )}
                </>
            ) : (
                <>
                    {selectedCapacity && (
                        <Tag
                            label="Capacity"
                            values={[selectedCapacity.name]}
                            sx={{ mb: 0.5 }}
                            valueProps={{
                                sx: {
                                    maxWidth: '88px',
                                    display: 'inline-flex',
                                    '& span': {
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    },
                                },
                            }}
                        />
                    )}
                    {selectedSchedule && (
                        <Tag
                            label="Schedule"
                            values={[selectedSchedule.name]}
                            sx={{ mb: 0.5 }}
                            valueProps={{
                                sx: {
                                    maxWidth: '88px',
                                    display: 'inline-flex',
                                    '& span': {
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    },
                                },
                            }}
                        />
                    )}
                    {sourceValue && (
                        <Tag
                            label="Source"
                            sx={{ mb: 0.5 }}
                            value={sourceValue}
                        />
                    )}
                </>
            )}
            {editing && !!dependencyOptions.length && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ mb: 1, width: '100%' }}
                    flexGrow={1}
                >
                    <Button
                        sx={{
                            bgcolor: '#FFFFFF',
                        }}
                        variant="outlined"
                        fullWidth
                        onClick={e => setAnchorEl(e.currentTarget)}
                        size="medium"
                    >
                        <Add />
                        {t(' Dependency')}
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={() => setAnchorEl(null)}
                    >
                        {dependencyOptions.map(opt => (
                            <MenuItem
                                key={opt.value}
                                onClick={() => {
                                    setAnchorEl(null);
                                    if (opt.value === 'source') {
                                        setSourceModalOpen(true);
                                    } else {
                                        setValue(
                                            `${formPrefix}${opt.value}`,
                                            {}
                                        );
                                    }
                                }}
                            >
                                {t(opt.label)}
                            </MenuItem>
                        ))}
                    </Menu>
                </Stack>
            )}
            <SourceModal
                open={sourceModalOpen}
                handleCancel={() => {
                    setSourceModalOpen(false);
                }}
                justifyContent={productId ? 'end' : 'center'}
                handleConfirm={s => {
                    setValue(`${formPrefix}source`, s, {
                        shouldDirty: true,
                    });
                    setSourceModalOpen(false);
                }}
            />
        </Stack>
    );
};

export default DependencySelection;
